<script setup>
import { storeToRefs } from 'pinia';

import { injectionKeyIsVerticalNavHovered, useLayouts } from '@layouts';
import { VerticalNavGroup, VerticalNavLink, VerticalNavSectionTitle } from '@layouts/components';
import { config } from '@layouts/config';
import { Menu, SquareMenu } from 'lucide-vue-next';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';

import MobileLogoIcon from '@/components/ui/icons/MobileLogo.vue';
import Logo from '@/components/ui/logo/Logo';
import { useConnectedChannels } from '@/composables/useConnectedChannels';
import { useNavItemVisibility } from '@/composables/useNavItemVisibility';
import { getOverviewRoute } from '@/router/utils';
import { useAccountStore } from '@/stores/account.js';
import { useAuthStore } from '@/stores/auth.js';

const props = defineProps({
  tag: {
    type: [String, null],
    required: false,
    default: 'aside',
  },
  navItems: {
    type: null,
    required: true,
  },
  isOverlayNavActive: {
    type: Boolean,
    required: true,
  },
  toggleIsOverlayNavActive: {
    type: Function,
    required: true,
  },
});

const refNav = ref();
const { width: windowWidth } = useWindowSize();

const isNavReady = ref(false);
const isHovered = ref(false);
const isActuallyHovered = useElementHover(refNav);
let hoverTimeout = null;

const authStore = useAuthStore();
const { isInitializingAuth } = storeToRefs(authStore);
const accountStore = useAccountStore();
const { ensureCurrentProfileLoaded } = accountStore;
const logoTarget = computed(() => getOverviewRoute(accountStore));

// Fetches eBay / Shopify connections (only where their nav items are env-visible) so the
// marketplace groups can resolve; re-runs by itself after a business-profile switch.
const { isEnvAllowed } = useNavItemVisibility();
useConnectedChannels({ isEnvAllowed, navItems: props.navItems }).startLoading();

// Wait for auth initialization to complete before loading profile
const loadProfileWhenReady = async () => {
  // If auth is initializing, wait for it to complete
  if (isInitializingAuth.value) {
    await new Promise((resolve) => {
      const unwatch = watch(isInitializingAuth, (val) => {
        if (!val) {
          unwatch();
          resolve();
        }
      }, { immediate: true });
    });
  }

  await ensureCurrentProfileLoaded();
  isNavReady.value = true;
};

loadProfileWhenReady();

watch(isActuallyHovered, (newValue) => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }

  if (newValue) {
    hoverTimeout = setTimeout(() => {
      isHovered.value = true;
    }, 500);
  } else {
    isHovered.value = false;
  }
});

onUnmounted(() => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
  }
});

provide(injectionKeyIsVerticalNavHovered, isHovered);

const {
  isLessThanOverlayNavBreakpoint,
  isAppRtl,
  isVerticalNavCollapsed,
} = useLayouts();

const resolveNavItemComponent = (item) => {
  if ('heading' in item) {
    return VerticalNavSectionTitle;
  }
  if ('children' in item) {
    return VerticalNavGroup;
  }
  return VerticalNavLink;
};

const route = useRoute();

watch(() => route.name, () => {
  props.toggleIsOverlayNavActive(false);
});

watch(() => isLessThanOverlayNavBreakpoint.value(windowWidth.value), (val) => {
  if (val) props.toggleIsOverlayNavActive(false);
});

const isVerticalNavScrolled = ref(false);
const isHideMenu = ref(false);
const updateIsVerticalNavScrolled = (val) => (isVerticalNavScrolled.value = val);

const handleNavScroll = (evt) => {
  isVerticalNavScrolled.value = evt.target.scrollTop > 0;
};

</script>

<template>
  <Component
    :is="props.tag"
    ref="refNav"
    class="layout-vertical-nav"
    :class="[
      {
        'overlay-nav': isLessThanOverlayNavBreakpoint(windowWidth),
        hovered: isHovered,
        visible: isOverlayNavActive,
        scrolled: isVerticalNavScrolled,
        hide: isHideMenu,
      },
    ]"
  >
    <!-- 👉 Header -->
    <div class="nav-header">
      <slot name="nav-header">
        <RouterLink
          :to="logoTarget"
          class="app-logo d-flex align-center gap-x-3 app-title-wrapper"
        >
          <Logo class="logo" />
        </RouterLink>
        <Component
          :is="config.app.iconRenderer || 'div'"
          v-if="isLessThanOverlayNavBreakpoint(windowWidth)"
          class="header-action"
          v-bind="config.icons.close"
          @click="toggleIsOverlayNavActive(false)"
        />
        <button
          v-else
          class="header-action nav-collapse-toggle"
          type="button"
          :aria-label="isVerticalNavCollapsed ? 'Expand navigation' : 'Collapse navigation'"
          :aria-expanded="!isVerticalNavCollapsed"
          @click="isVerticalNavCollapsed = !isVerticalNavCollapsed"
        >
          <MobileLogoIcon class="nav-collapse-logo" />
          <Component :is="isVerticalNavCollapsed ? Menu : SquareMenu" class="nav-collapse-symbol" :size="20" />
        </button>
      </slot>
    </div>
    <slot name="before-nav-items">
      <div class="vertical-nav-items-shadow" />
    </slot>
    <slot
      name="nav-items"
      :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
    >
      <PerfectScrollbar
        v-if="isNavReady"
        :key="isAppRtl"
        tag="ul"
        class="nav-items"
        :options="{ wheelPropagation: false }"
        @ps-scroll-y="handleNavScroll"
      >
        <Component
          :is="resolveNavItemComponent(item)"
          v-for="(item, index) in navItems"
          :key="index"
          :item="item"
        />
      </PerfectScrollbar>
    </slot>
    <slot name="after-nav-items" />
  </Component>
</template>

<style lang="scss">
@use '@configured-variables' as variables;
@use '@layouts/styles/mixins';

.layout-vertical-nav {
  position: fixed;
  z-index: variables.$layout-vertical-nav-z-index;
  display: flex;
  flex-direction: column;
  block-size: calc(100% - var(--notification-bar-height, 0px));
  inline-size: variables.$layout-vertical-nav-width;
  inset-block-start: var(--notification-bar-height, 0px);
  inset-inline-start: 0;
  transition:
    transform 0.25s ease-in-out,
    inline-size 0.25s ease-in-out,
    box-shadow 0.25s ease-in-out;
  will-change: transform, inline-size;

  &.vertical-nav-collapsed {
    inline-size: variables.$layout-vertical-nav-collapsed-width;
  }

  .nav-header {
    display: flex;
    align-items: center;

    .header-action {
      display: block !important;
      cursor: pointer;
      color: rgb(var(--v-theme-text-color));
    }
  }

  .app-title-wrapper {
    margin-inline-end: auto;
  }

  // Fills the space between the header and the pinned `after-nav-items` slot; min-block-size: 0
  // lets it shrink so the slot content is never pushed out of the aside.
  .nav-items {
    flex: 1 1 0;
    min-block-size: 0;
  }

  .nav-item-title {
    overflow: hidden;
    margin-inline-end: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .layout-vertical-nav-collapsed & {
    &:not(.hovered) {
      inline-size: variables.$layout-vertical-nav-collapsed-width;
    }
    &.hide {
      inline-size: variables.$layout-vertical-nav-collapsed-width;
    }
  }

  &.overlay-nav {
    &:not(.visible) {
      transform: translateX(-#{variables.$layout-vertical-nav-width});

      @include mixins.rtl {
        transform: translateX(variables.$layout-vertical-nav-width);
      }
    }
  }
}
</style>

<style lang="scss">
.nav-collapse-logo { display: none; }

.layout-vertical-nav .nav-header .nav-collapse-toggle {
  // !important: `.header-action { display: block !important }` above would otherwise win and
  // drop the icon onto the text baseline, off-centre against the logo.
  display: inline-flex !important;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--wp-text-primary);
}
</style>
